import { useTranslation } from 'react-i18next'
import AccountFlowCard from './AccountFlowCard'
import { useAdvance } from '../hooks/use-advance'
import { formatCurrency } from 'utils/executive.utils'

export default function AdvanceCard() {
  const { t } = useTranslation()
  const { data, loading, error, refetch } = useAdvance()

  // Receipt counts shown next to the inflow/outflow amounts, plus two
  // performance metrics below — all derived from the advance headers and
  // built only when data is present.
  const receipts = (n: number) =>
    t('EXECUTIVE.ACCOUNT.ADVANCE.RECEIPTS', {
      n: n.toLocaleString('th-TH'),
      defaultValue: '{{n}} รายการ',
    })

  const metrics = data
    ? [
        {
          label: t('EXECUTIVE.ACCOUNT.ADVANCE.AVG_RECEIVE', 'เฉลี่ย/รายการรับ'),
          value: data.avgReceive != null ? formatCurrency(data.avgReceive) : '—',
        },
        {
          label: t('EXECUTIVE.ACCOUNT.ADVANCE.OUT_RATIO', 'สัดส่วนใช้/คืน'),
          value: data.refundRate != null ? `${(data.refundRate * 100).toFixed(1)}%` : '—',
        },
      ]
    : undefined

  // "Received from which branches, via which channels" — each branch is a
  // header row (amount + share of total inflow), with its payment channels
  // listed beneath as sub-rows (cash / transfer / card / wallet).
  const breakdown =
    data && data.byBranch.length > 0
      ? {
          title: t('EXECUTIVE.ACCOUNT.ADVANCE.BY_BRANCH_CHANNEL', 'รับเข้าแยกตามสาขา · ช่องทาง'),
          rows: data.byBranch.flatMap((b) => [
            {
              label: b.branchName,
              value: `${formatCurrency(b.received)} · ${(b.share * 100).toFixed(1)}%`,
            },
            ...b.channels.map((c) => ({
              label:
                c.channelName ||
                t('EXECUTIVE.ACCOUNT.ADVANCE.CHANNEL_UNSPECIFIED', 'ไม่ระบุช่องทาง'),
              value: formatCurrency(c.received),
              sub: true,
            })),
          ]),
        }
      : undefined

  return (
    <AccountFlowCard
      title={t('EXECUTIVE.ACCOUNT.ADVANCE.TITLE')}
      inflowLabel={t('EXECUTIVE.ACCOUNT.ADVANCE.INFLOW')}
      outflowLabel={t('EXECUTIVE.ACCOUNT.ADVANCE.OUTFLOW')}
      tooltip={t('EXECUTIVE.ACCOUNT.ADVANCE.TOOLTIP')}
      inflowNote={data ? receipts(data.receiveCount) : undefined}
      outflowNote={data ? receipts(data.refundCount) : undefined}
      metrics={metrics}
      breakdown={breakdown}
      data={data}
      loading={loading}
      error={error}
      onRetry={refetch}
    />
  )
}
